import type { Row, Workbook } from "exceljs";

import {
  looksLikeHeader,
  detectColumnType,
} from "@/app/_module/utils/spreadsheetDetect";

/**
 * Parse an .xlsx IN THE BROWSER, the way CSV already is.
 *
 * WHY THIS EXISTS: the file is already on the user's machine when they pick it, so
 * uploading it to a server, parsing it there and shipping every row back was a round
 * trip to reach a state we already had. Worse, that server is the small one — a single
 * spreadsheet expanded past its heap and took the whole site down twice in 90 seconds
 * (2026-09-14). Parsing here costs the server nothing, needs no upload, and the worst
 * a pathological file can do is make one browser tab slow.
 *
 * Detection is imported, never re-implemented: a headerless file must map the same
 * whichever parser reads it.
 */

type Cell = string | number | boolean | null;

// ExcelJS row.values is 1-indexed with a leading hole.
function rowValues(row: Row): Cell[] {
  const values: Cell[] = [];
  const raw = row.values as unknown[];
  for (let i = 1; i < raw.length; i++) {
    const cell = raw[i];
    if (cell === null || cell === undefined) values.push(null);
    else if (cell instanceof Date) values.push(cell.toISOString().slice(0, 10));
    else if (typeof cell === "object") {
      // rich text / formula / hyperlink cells
      const o = cell as { text?: string; result?: unknown; richText?: { text: string }[] };
      if (Array.isArray(o.richText)) values.push(o.richText.map((r) => r.text).join(""));
      else if (o.text !== undefined) values.push(o.text);
      else if (o.result !== undefined) values.push(o.result as Cell);
      else values.push(null);
    } else values.push(cell as string | number | boolean);
  }
  return values;
}

// Header row or not? Same rule the server used.
const isHeaderRow = (row: Cell[]) =>
  row.filter((v) => v !== null && v !== "").some((v) => looksLikeHeader(v));

/** Tabs holding data rows, in workbook order. Hidden tabs count only if nothing visible has rows. */
function tabsWithRows(workbook: Workbook) {
  const tabs = workbook.worksheets.flatMap((ws) => {
    let first: Row | undefined;
    for (let r = 1; r <= ws.rowCount && !first; r++) {
      const row = ws.findRow(r);
      if (row?.hasValues) first = row;
    }
    if (!first) return [];
    const rows = ws.actualRowCount - (isHeaderRow(rowValues(first)) ? 1 : 0);
    return rows > 0 ? [{ ws, name: ws.name, rows }] : [];
  });
  const visible = tabs.filter((t) => t.ws.state === "visible");
  return visible.length ? visible : tabs;
}

/** One cap for CSV and Excel (UploadCSV reads it too); = MAX_IMPORT_ROW_LIMIT on the backend. */
export const MAX_IMPORT_ROWS = 25000;

export interface ParsedSpreadsheet {
  headers: string[];
  /** One object per row, keyed by header — the shape the mapping step expects. */
  data: Record<string, unknown>[];
  hasHeaders: boolean;
  detectedTypes: Record<string, string> | null;
}

export class SpreadsheetTooLargeError extends Error {}

const TYPE_LABELS: Record<string, string> = {
  email: "Email", phone: "Phone", state: "State", zipCode: "Zip Code",
  date: "Date", dob: "Date of Birth", yesNo: "Yes/No", firstName: "First Name",
  lastName: "Last Name", city: "City", address: "Address", gender: "Gender",
  marital: "Marital Status", employment: "Employment", smoker: "Smoker",
  insuranceType: "Insurance Type", beneficiary: "Beneficiary",
  latitude: "Latitude", longitude: "Longitude",
};

export interface SheetTab {
  name: string;
  /** Data rows, not counting a header row. */
  rows: number;
}

/** Rows on more than one tab and no tab named: the caller asks which. */
export interface TabChoice {
  tabs: SheetTab[];
}

/**
 * Name columns by what their VALUES look like. Used for a headerless workbook and for
 * a CSV that arrived with auto-generated labels ("Column 1", "Column 8 (City)") — a
 * re-export of a previous headerless import, which papaparse would otherwise follow
 * straight into the wrong mapping.
 *
 * Takes rows that are ALREADY parsed, so the CSV path re-detects in place instead of
 * uploading the file somewhere to be read a second time.
 */
export function detectHeadersFromRows(
  sampleRows: (string | number | boolean | null)[][],
  columnCount: number,
): { headers: string[]; detectedTypes: Record<string, string> | null } {
  const headers: string[] = [];
  const detectedTypes: Record<string, string> = {};
  for (let col = 0; col < columnCount; col++) {
    const columnValues = sampleRows.map((r) => r[col] ?? null);
    const { type } = detectColumnType(columnValues, col);
    let headerName = `Column ${col + 1}`;
    if (type) {
      headerName = `Column ${col + 1} (${TYPE_LABELS[type] || type})`;
      // keyed by the PLAIN name, exactly as the server did
      detectedTypes[`Column ${col + 1}`] = type;
    }
    headers.push(headerName);
  }
  return { headers, detectedTypes: Object.keys(detectedTypes).length ? detectedTypes : null };
}

/**
 * Rows on more than one tab and no `tab` given: returns the tabs, not rows, so the
 * caller can ask. It used to read the first tab and say nothing: Moe Ray's workbook
 * (250 leads on tab one, 2,000 on the next) imported 250 (2026-10-07).
 */
export function parseSpreadsheetInBrowser(file: File): Promise<ParsedSpreadsheet | TabChoice>;
export function parseSpreadsheetInBrowser(file: File, tab: string): Promise<ParsedSpreadsheet>;
export async function parseSpreadsheetInBrowser(file: File, tab?: string): Promise<ParsedSpreadsheet | TabChoice> {
  // Loaded on demand: ExcelJS is large and only import users ever need it, so it must
  // not sit in the main bundle. ExcelJS declares a browser build in its `browser`
  // field, so the bundler picks the right one without us naming a dist path.
  // exceljs is CJS. Depending on bundler and interop settings the namespace is either
  // the module itself or wrapped in `.default` — take whichever actually carries
  // Workbook rather than assuming, because getting it wrong only fails at runtime.
  const mod = (await import("exceljs")) as unknown as Record<string, unknown>;
  const ExcelJS = (("Workbook" in mod ? mod : (mod.default as Record<string, unknown>)) ??
    mod) as unknown as typeof import("exceljs");
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const tabs = tabsWithRows(workbook);
  if (tabs.length === 0) throw new Error("That file looks empty — there are no rows to import.");
  if (tab === undefined && tabs.length > 1) return { tabs: tabs.map(({ name, rows }) => ({ name, rows })) };
  const picked = tab === undefined ? tabs[0] : tabs.find((t) => t.name === tab);
  if (!picked) throw new Error(`That workbook has no "${tab}" tab with rows in it.`);

  // Bail on row count BEFORE building objects for every row — the guard is pointless
  // if we've already paid the memory to break it.
  if (picked.rows > MAX_IMPORT_ROWS) {
    throw new SpreadsheetTooLargeError(
      `${tab ? `The "${tab}" tab` : "This file"} has ${picked.rows.toLocaleString()} rows, which is over the ${MAX_IMPORT_ROWS.toLocaleString()} limit for one import. Split it into smaller files and upload them one at a time.`,
    );
  }

  // Worksheet -> plain 2D array, trailing blank rows dropped.
  const rows: Cell[][] = [];
  picked.ws.eachRow({ includeEmpty: false }, (row) => {
    rows.push(rowValues(row));
  });

  const firstRow = rows[0];
  const hasHeaders = isHeaderRow(firstRow);

  let headers: string[];
  let bodyRows: (string | number | boolean | null)[][];
  let detectedTypes: Record<string, string> | null = null;

  if (hasHeaders) {
    headers = firstRow.map((v, i) => String(v ?? "").trim() || `Column ${i + 1}`);
    bodyRows = rows.slice(1);
  } else {
    // Headerless: name each column by what its VALUES look like, so the mapping step
    // has something real to work with instead of "Column 1..N". Same 20-row sample and
    // same labels the server used — a file must map identically on either parser.
    bodyRows = rows;
    const columnCount = Math.max(...rows.map((r) => r.length));
    const sampleData = bodyRows.slice(0, 20);
    const detected = detectHeadersFromRows(sampleData, columnCount);
    headers = detected.headers;
    detectedTypes = detected.detectedTypes;
  }

  const data = bodyRows.map((row) => {
    const record: Record<string, unknown> = {};
    headers.forEach((h, i) => { record[h] = row[i] ?? ""; });
    return record;
  });

  return { headers, data, hasHeaders, detectedTypes };
}
